import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";


const TOAST_DURATION = 4000;
let _toastSetter = null;
const toast = {
  _push: (item) => {
    const id = Date.now() + Math.random();
    _toastSetter?.((p) => [...p, { id, ...item }]);
    return id;
  },
  success: (title, msg) => toast._push({ type: "success", title, msg }),
  error: (title, msg) => toast._push({ type: "error", title, msg }),
  info: (title, msg) => toast._push({ type: "info", title, msg }),
  loading: (title) => toast._push({ type: "loading", title, persistent: true }),
  dismiss: (id) => _toastSetter?.((p) => p.filter((t) => t.id !== id)),
};
const TOAST_META = {
  success: { bar: "#16a34a", icon: "✓" },
  error: { bar: "#dc2626", icon: "✕" },
  info: { bar: "#1d4ed8", icon: "i" },
  loading: { bar: "#94a3b8", icon: null },
};
const CloseIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const ToastItem = ({ t, onDismiss }) => {
  const meta = TOAST_META[t.type];
  const [progress, setProgress] = useState(100);
  const [exiting, setExiting] = useState(false);
  const rafRef = useRef(null);
  const startTs = useRef(Date.now());
  const dismiss = useCallback(() => {
    setExiting(true);
    setTimeout(() => onDismiss(t.id), 280);
  }, [t.id, onDismiss]);
  useEffect(() => {
    if (t.persistent) return;
    const tick = () => {
      const pct = Math.max(
        0,
        100 - ((Date.now() - startTs.current) / TOAST_DURATION) * 100,
      );
      setProgress(pct);
      if (pct > 0) rafRef.current = requestAnimationFrame(tick);
      else dismiss();
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [t.persistent, dismiss]);
  return (
    <div
      style={{
        position: "relative",
        overflow: "hidden",
        background: "#fff",
        borderRadius: 10,
        borderLeft: `4px solid ${meta.bar}`,
        boxShadow: "0 4px 20px rgba(0,0,0,0.10), 0 0 0 1px rgba(0,0,0,0.06)",
        minWidth: 300,
        maxWidth: 360,
        animation: exiting
          ? "dash-out 0.28s ease forwards"
          : "dash-in 0.28s cubic-bezier(0.34,1.56,0.64,1) forwards",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
          padding: "13px 14px 14px",
        }}
      >
        <div
          style={{
            flexShrink: 0,
            width: 24,
            height: 24,
            borderRadius: "50%",
            background: `${meta.bar}18`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginTop: 1,
          }}
        >
          {t.type === "loading" ? (
            <svg
              width="13"
              height="13"
              viewBox="0 0 13 13"
              fill="none"
              style={{ animation: "dash-spin 0.85s linear infinite" }}
            >
              <circle
                cx="6.5"
                cy="6.5"
                r="5"
                stroke={meta.bar}
                strokeWidth="2"
                strokeDasharray="8 20"
                strokeLinecap="round"
              />
            </svg>
          ) : (
            <span
              style={{
                fontSize: 11,
                fontWeight: 800,
                color: meta.bar,
                lineHeight: 1,
              }}
            >
              {meta.icon}
            </span>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{
              margin: 0,
              fontSize: 13.5,
              fontWeight: 650,
              color: "#0f172a",
              lineHeight: 1.35,
            }}
          >
            {t.title}
          </p>
          {t.msg && (
            <p
              style={{
                margin: "3px 0 0",
                fontSize: 12.5,
                color: "#64748b",
                lineHeight: 1.5,
              }}
            >
              {t.msg}
            </p>
          )}
        </div>
        <button
          onClick={dismiss}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "#cbd5e1",
            padding: "2px",
            flexShrink: 0,
            display: "flex",
            borderRadius: 4,
          }}
        >
          <CloseIcon />
        </button>
      </div>
      {!t.persistent && (
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 3,
            background: "#f1f5f9",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${progress}%`,
              background: meta.bar,
              transition: "width 0.1s linear",
              borderRadius: "0 2px 0 0",
            }}
          />
        </div>
      )}
    </div>
  );
};
const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);
  _toastSetter = setToasts;
  const dismiss = useCallback(
    (id) => setToasts((p) => p.filter((t) => t.id !== id)),
    [],
  );
  if (!toasts.length) return null;
  return (
    <div
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        alignItems: "flex-end",
      }}
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} t={t} onDismiss={dismiss} />
      ))}
    </div>
  );
};

const RefreshIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);
const UsersIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const StoreIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);
const FileTextIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);
const MessageIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);
const PackageIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <line x1="16.5" y1="9.4" x2="7.5" y2="4.21" />
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" y1="22.08" x2="12" y2="12" />
  </svg>
);
const DatabaseIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <ellipse cx="12" cy="5" rx="9" ry="3" />
    <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
  </svg>
);
const CloudIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
  </svg>
);
const TrendUpIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
  >
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);
const TrendDownIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
  >
    <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" />
    <polyline points="17 18 23 18 23 12" />
  </svg>
);
const CheckCircleIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);
const LoaderIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    style={{ animation: "dash-spin 0.8s linear infinite" }}
  >
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
  </svg>
);
const AlertIcon = () => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return (
    d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }) +
    " " +
    d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
  );
};
const fmtShortDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const toNonNegativeInt = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n);
};

const pickFirstCount = (...candidates) => {
  for (const candidate of candidates) {
    const parsed = toNonNegativeInt(candidate);
    if (parsed !== null) return { value: parsed, found: true };
  }
  return { value: 0, found: false };
};

const getStatusCount = (items, keys = []) => {
  if (!Array.isArray(items)) return null;
  for (const item of items) {
    const itemKey = String(item?.key || "").toLowerCase();
    if (!itemKey) continue;
    if (keys.some((k) => itemKey.includes(k))) {
      const parsed = toNonNegativeInt(item?.count);
      if (parsed !== null) return parsed;
    }
  }
  return null;
};

const deriveDashboardExtraStats = (payload) => {
  const rfqItems = payload?.rfq_status?.items || [];

  const shortlisted = pickFirstCount(
    payload?.overview?.shortlisted_rfqs,
    payload?.rfq_status?.shortlisted,
    payload?.rfq_status?.shortlisted_rfqs,
    payload?.rfq_metrics?.shortlisted,
    payload?.rfq_metrics?.shortlisted_rfqs,
    getStatusCount(rfqItems, ["shortlist", "shortlisted"]),
  );

  const bids = pickFirstCount(
    payload?.overview?.total_bids,
    payload?.overview?.vendor_bids,
    payload?.rfq_status?.total_bids,
    payload?.rfq_metrics?.total_bids,
    payload?.rfq_metrics?.bids,
    payload?.system_stats?.total_bids,
    payload?.system_stats?.bids,
  );

  const approvalsCreated = pickFirstCount(
    payload?.overview?.approval_created,
    payload?.overview?.approvals_created,
    payload?.approval_status?.created,
    payload?.approval_status?.total_created,
    payload?.approvals?.created,
    payload?.approvals?.total_created,
  );

  const approvalsNotApproved = pickFirstCount(
    payload?.overview?.approval_not_approved,
    payload?.overview?.approvals_not_approved,
    payload?.approval_status?.not_approved,
    payload?.approval_status?.pending,
    payload?.approval_status?.rejected,
    payload?.approvals?.not_approved,
    payload?.approvals?.pending,
    payload?.approvals?.rejected,
  );

  const hasBackendValues =
    shortlisted.found ||
    bids.found ||
    approvalsCreated.found ||
    approvalsNotApproved.found;

  return {
    shortlisted: shortlisted.value,
    bids: bids.value,
    approvalsCreated: approvalsCreated.value,
    approvalsNotApproved: approvalsNotApproved.value,
    hasBackendValues,
  };
};

const Skel = ({ w = "100%", h = 14, r = 6 }) => (
  <div
    style={{
      width: w,
      height: h,
      borderRadius: r,
      background: "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)",
      backgroundSize: "200% 100%",
      animation: "dash-shimmer 1.4s infinite",
    }}
  />
);

const SectionCard = ({
  title,
  subtitle,
  icon,
  children,
  accentColor = "#3b82f6",
  style = {},
}) => (
  <div
    style={{
      background: "#fff",
      borderRadius: 14,
      border: "1px solid #e2e8f0",
      boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
      overflow: "hidden",
      ...style,
    }}
  >
    <div
      style={{
        padding: "16px 20px 14px",
        borderBottom: "1px solid #f1f5f9",
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}
    >
      <div
        style={{
          width: 34,
          height: 34,
          borderRadius: 9,
          background: `${accentColor}14`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: accentColor,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div>
        <p
          style={{
            margin: 0,
            fontSize: 14,
            fontWeight: 700,
            color: "#0f172a",
            lineHeight: 1.2,
          }}
        >
          {title}
        </p>
        {subtitle && (
          <p style={{ margin: "2px 0 0", fontSize: 12, color: "#94a3b8" }}>
            {subtitle}
          </p>
        )}
      </div>
    </div>
    <div style={{ padding: "18px 20px" }}>{children}</div>
  </div>
);

const OverviewCard = ({
  label,
  value,
  sub,
  icon,
  color,
  bg,
  border,
  loading,
  onClick,
}) => (
  <div
    style={{
      background: bg,
      border: `1px solid ${border}`,
      borderRadius: 12,
      padding: "16px 18px",
      display: "flex",
      alignItems: "flex-start",
      gap: 12,
      cursor: onClick ? "pointer" : "default",
    }}
    onClick={onClick}
  >
    <div
      style={{
        width: 38,
        height: 38,
        borderRadius: 10,
        background: `${color}20`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color,
        flexShrink: 0,
      }}
    >
      {icon}
    </div>
    <div style={{ minWidth: 0, flex: 1 }}>
      {loading ? (
        <>
          <Skel w={44} h={22} r={5} />
          <div style={{ marginTop: 5 }}>
            <Skel w={80} h={11} />
          </div>
        </>
      ) : (
        <>
          <p
            style={{
              margin: 0,
              fontSize: 22,
              fontWeight: 800,
              color: "#0f172a",
              lineHeight: 1.1,
            }}
          >
            {value?.toLocaleString()}
          </p>
          {sub !== undefined && (
            <p style={{ margin: "2px 0 0", fontSize: 12, color }}>{sub}</p>
          )}
        </>
      )}
      <p
        style={{
          margin: "4px 0 0",
          fontSize: 12,
          color: "#64748b",
          fontWeight: 550,
          letterSpacing: "0.01em",
        }}
      >
        {label}
      </p>
    </div>
  </div>
);

const FunnelItem = ({ label, count, total, color }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div style={{ marginBottom: 12 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 5,
        }}
      >
        <span
          style={{
            fontSize: 12.5,
            color: "#374151",
            fontWeight: 550,
            flex: 1,
            paddingRight: 8,
          }}
        >
          {label}
        </span>
        <span
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: "#0f172a",
            minWidth: 28,
            textAlign: "right",
          }}
        >
          {count}
        </span>
      </div>
      <div
        style={{
          height: 7,
          background: "#f1f5f9",
          borderRadius: 6,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${pct}%`,
            background: color,
            borderRadius: 6,
            transition: "width 0.8s cubic-bezier(0.16,1,0.3,1)",
          }}
        />
      </div>
    </div>
  );
};

const RFQ_COLORS = {
  published: { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe" },
  opened: { bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0" },
  bid_closed: { bg: "#fefce8", color: "#d97706", border: "#fde68a" },
  awarded: { bg: "#f5f3ff", color: "#7c3aed", border: "#ddd6fe" },
  cancelled: { bg: "#fff5f5", color: "#dc2626", border: "#fecaca" },
};
const RFQStatusItem = ({ item, total }) => {
  const c = RFQ_COLORS[item.key] || {
    bg: "#f8fafc",
    color: "#64748b",
    border: "#e2e8f0",
  };
  const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 0",
        borderBottom: "1px solid #f8fafc",
      }}
    >
      <div
        style={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          background: c.color,
          flexShrink: 0,
        }}
      />
      <span
        style={{ fontSize: 13, color: "#374151", fontWeight: 550, flex: 1 }}
      >
        {item.label}
      </span>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div
          style={{
            width: 80,
            height: 6,
            background: "#f1f5f9",
            borderRadius: 4,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${pct}%`,
              background: c.color,
              borderRadius: 4,
              transition: "width 0.8s cubic-bezier(0.16,1,0.3,1)",
            }}
          />
        </div>
        <span
          style={{
            display: "inline-flex",
            padding: "2px 10px",
            borderRadius: 20,
            fontSize: 12,
            fontWeight: 700,
            background: c.bg,
            color: c.color,
            border: `1px solid ${c.border}`,
            minWidth: 28,
            justifyContent: "center",
          }}
        >
          {item.count}
        </span>
      </div>
    </div>
  );
};

const QuickStatCard = ({ stat, loading }) => {
  const hasGrowth7d = stat.growth_7d !== null && stat.growth_7d !== undefined;
  const hasGrowth30d =
    stat.growth_30d !== null && stat.growth_30d !== undefined;
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: 12,
        padding: "14px 16px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      }}
    >
      {loading ? (
        <>
          <Skel w="60%" h={18} />
          <div style={{ marginTop: 8 }}>
            <Skel w="40%" h={11} />
          </div>
          <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
            <Skel w={60} h={24} r={8} />
            <Skel w={60} h={24} r={8} />
          </div>
        </>
      ) : (
        <>
          <p
            style={{
              margin: "0 0 2px",
              fontSize: 20,
              fontWeight: 800,
              color: "#0f172a",
            }}
          >
            {stat.total?.toLocaleString()}
          </p>
          <p
            style={{
              margin: "0 0 10px",
              fontSize: 12,
              color: "#64748b",
              fontWeight: 600,
            }}
          >
            {stat.label}
          </p>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {hasGrowth7d && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "3px 9px",
                  borderRadius: 7,
                  fontSize: 11.5,
                  fontWeight: 700,
                  background:
                    stat.growth_7d > 0
                      ? "#f0fdf4"
                      : stat.growth_7d < 0
                        ? "#fff5f5"
                        : "#f8fafc",
                  color:
                    stat.growth_7d > 0
                      ? "#16a34a"
                      : stat.growth_7d < 0
                        ? "#dc2626"
                        : "#94a3b8",
                  border: `1px solid ${stat.growth_7d > 0 ? "#bbf7d0" : stat.growth_7d < 0 ? "#fecaca" : "#e2e8f0"}`,
                }}
              >
                {stat.growth_7d > 0 ? (
                  <TrendUpIcon />
                ) : stat.growth_7d < 0 ? (
                  <TrendDownIcon />
                ) : null}
                {stat.growth_7d > 0 ? "+" : ""}
                {stat.growth_7d} 7d
              </span>
            )}
            {hasGrowth30d && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "3px 9px",
                  borderRadius: 7,
                  fontSize: 11.5,
                  fontWeight: 700,
                  background:
                    stat.growth_30d > 0
                      ? "#eff6ff"
                      : stat.growth_30d < 0
                        ? "#fff5f5"
                        : "#f8fafc",
                  color:
                    stat.growth_30d > 0
                      ? "#2563eb"
                      : stat.growth_30d < 0
                        ? "#dc2626"
                        : "#94a3b8",
                  border: `1px solid ${stat.growth_30d > 0 ? "#bfdbfe" : stat.growth_30d < 0 ? "#fecaca" : "#e2e8f0"}`,
                }}
              >
                {stat.growth_30d > 0 ? (
                  <TrendUpIcon />
                ) : stat.growth_30d < 0 ? (
                  <TrendDownIcon />
                ) : null}
                {stat.growth_30d > 0 ? "+" : ""}
                {stat.growth_30d} 30d
              </span>
            )}
            {!hasGrowth7d && !hasGrowth30d && (
              <span style={{ fontSize: 11.5, color: "#cbd5e1" }}>
                No growth data
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const MASTER_ITEMS = [
  { key: "categories", label: "Categories", color: "#7c3aed" },
  { key: "subcategories", label: "Sub Categories", color: "#2563eb" },
  { key: "business_types", label: "Business Types", color: "#0891b2" },
  { key: "designations", label: "Designations", color: "#d97706" },
  { key: "banks", label: "Banks", color: "#16a34a" },
  { key: "countries", label: "Countries", color: "#dc2626" },
  { key: "states", label: "States", color: "#ea580c" },
  { key: "cities", label: "Cities", color: "#0d9488" },
  { key: "validities", label: "Validities", color: "#7c3aed" },
  { key: "business_subtypes", label: "Business Types", color: "#64748b" },
];

const STAGE_COLORS = {
  submitted: { bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0" },
  step4_financial: { bg: "#fefce8", color: "#d97706", border: "#fde68a" },
  step4_bank_details: { bg: "#fef9c3", color: "#ca8a04", border: "#fde047" },
  step3_documents: { bg: "#fff7ed", color: "#ea580c", border: "#fed7aa" },
  step3_contact: { bg: "#fff7ed", color: "#c2410c", border: "#fdba74" },
  step2_gst: { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe" },
  step2_pan: { bg: "#eff6ff", color: "#1d4ed8", border: "#93c5fd" },
  step1_basic: { bg: "#f8fafc", color: "#64748b", border: "#e2e8f0" },
};
const StageBadge = ({ stage, label }) => {
  const c = STAGE_COLORS[stage] || {
    bg: "#f8fafc",
    color: "#64748b",
    border: "#e2e8f0",
  };
  return (
    <span
      style={{
        display: "inline-flex",
        padding: "3px 9px",
        borderRadius: 20,
        fontSize: 11.5,
        fontWeight: 600,
        background: c.bg,
        color: c.color,
        border: `1px solid ${c.border}`,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
};

const RFQBadge = ({ status, label }) => {
  const c = RFQ_COLORS[status] || {
    bg: "#f8fafc",
    color: "#64748b",
    border: "#e2e8f0",
  };
  return (
    <span
      style={{
        display: "inline-flex",
        padding: "3px 9px",
        borderRadius: 20,
        fontSize: 11.5,
        fontWeight: 600,
        background: c.bg,
        color: c.color,
        border: `1px solid ${c.border}`,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
};

const StorageBar = ({
  label,
  icon,
  enabled,
  sizePretty,
  sizeBytes,
  color,
  error,
  extraInfo,
}) => (
  <div
    style={{
      background: enabled ? "#f8fafc" : "#fafafa",
      border: `1px solid ${enabled ? "#e2e8f0" : "#f1f5f9"}`,
      borderRadius: 10,
      padding: "14px 16px",
      opacity: enabled ? 1 : 0.65,
    }}
  >
    <div
      style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}
    >
      <span style={{ color: enabled ? color : "#94a3b8" }}>{icon}</span>
      <span
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: enabled ? "#0f172a" : "#94a3b8",
        }}
      >
        {label}
      </span>
      <span
        style={{
          marginLeft: "auto",
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          padding: "2px 8px",
          borderRadius: 20,
          fontSize: 11,
          fontWeight: 700,
          background: enabled ? "#f0fdf4" : "#f8fafc",
          color: enabled ? "#16a34a" : "#94a3b8",
          border: `1px solid ${enabled ? "#bbf7d0" : "#e2e8f0"}`,
        }}
      >
        {enabled ? <CheckCircleIcon /> : null}
        {enabled ? "Enabled" : "Disabled"}
      </span>
    </div>
    {error ? (
      <p style={{ margin: 0, fontSize: 12, color: "#dc2626" }}>{error}</p>
    ) : (
      <p
        style={{
          margin: 0,
          fontSize: 18,
          fontWeight: 800,
          color: enabled ? "#0f172a" : "#94a3b8",
          lineHeight: 1,
        }}
      >
        {sizePretty}
      </p>
    )}
    {extraInfo && (
      <p style={{ margin: "4px 0 0", fontSize: 11.5, color: "#94a3b8" }}>
        {extraInfo}
      </p>
    )}
  </div>
);

const TAB_LIST = ["Vendors", "RFQs", "Contact Requests"];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actTab, setActTab] = useState(0);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const { data: json } = await axiosInstance.get(
        "/api/superadmin/dashboard/",
      );
      setData(json?.data ?? null);
      setLastRefresh(new Date());
      if (silent)
        toast.success("Dashboard Refreshed", "All metrics have been updated.");
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        status === 403
          ? "Access denied. Admin privileges required."
          : !err.response
            ? "Network error. Check your connection."
            : `Failed to load dashboard (${status}).`;
      setError(msg);
      if (silent)
        toast.error("Refresh Failed", "Showing last loaded data. " + msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const ov = data?.overview || {};
  const vo = data?.vendor_onboarding || {};
  const rs = data?.rfq_status || {};
  const md = data?.master_data || {};
  const ra = data?.recent_activity || {};
  const ss = data?.system_stats || {};
  const extraStats = deriveDashboardExtraStats(data);

  return (
    <div
      style={{
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        background: "#f8fafc",
        minHeight: "100vh",
        color: "#1e293b",
      }}
    >
      <style>{`
        @keyframes dash-shimmer  { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
        @keyframes dash-fade-up  { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes dash-in       { from { opacity: 0; transform: translateX(24px) scale(0.97); } to { opacity: 1; transform: translateX(0) scale(1); } }
        @keyframes dash-out      { from { opacity: 1; transform: translateX(0) scale(1); } to { opacity: 0; transform: translateX(28px) scale(0.95); } }
        @keyframes dash-spin     { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .dash-row:hover { background: #f8fafc !important; }
        * { box-sizing: border-box; }
      `}</style>

      <ToastContainer />

      <div
        style={{
          padding: "14px 28px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 10,
          position: "sticky",
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 800,
              color: "#0f172a",
              letterSpacing: "-0.02em",
            }}
          >
            Dashboard
          </h1>
          <p style={{ margin: "2px 0 0", fontSize: 12, color: "#94a3b8" }}>
            {lastRefresh
              ? `Last refreshed: ${lastRefresh.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}`
              : ss.computed_at
                ? `Data computed at: ${fmtDate(ss.computed_at)}`
                : "System overview and metrics"}
          </p>
        </div>
        <button
          onClick={() => load(true)}
          disabled={loading}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 18px",
            background: loading ? "#f1f5f9" : "#fff",
            color: loading ? "#94a3b8" : "#374151",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            fontSize: 13.5,
            fontWeight: 600,
            cursor: loading ? "not-allowed" : "pointer",
            transition: "background 0.15s",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          }}
          onMouseEnter={(e) => {
            if (!loading) e.currentTarget.style.background = "#f8fafc";
          }}
          onMouseLeave={(e) => {
            if (!loading) e.currentTarget.style.background = "#fff";
          }}
        >
          {loading ? <LoaderIcon /> : <RefreshIcon />}
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {error && !loading && !data && (
        <div
          style={{
            margin: "24px 28px",
            background: "#fff5f5",
            border: "1px solid #fecaca",
            borderRadius: 12,
            padding: "24px",
            display: "flex",
            alignItems: "center",
            gap: 16,
          }}
        >
          <div style={{ color: "#dc2626", flexShrink: 0 }}>
            <AlertIcon />
          </div>
          <div>
            <p
              style={{
                margin: "0 0 4px",
                fontSize: 15,
                fontWeight: 700,
                color: "#dc2626",
              }}
            >
              Failed to Load Dashboard
            </p>
            <p style={{ margin: "0 0 12px", fontSize: 13.5, color: "#64748b" }}>
              {error}
            </p>
            <button
              onClick={() => load()}
              style={{
                padding: "8px 20px",
                background: "#dc2626",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                fontSize: 13.5,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Try Again
            </button>
          </div>
        </div>
      )}

      <div
        style={{
          padding: "24px 28px",
          display: "flex",
          flexDirection: "column",
          gap: 20,
          animation: "dash-fade-up 0.3s ease",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: 12,
          }}
        >
          <OverviewCard
            label="Total Users"
            value={ov.total_users}
            icon={<UsersIcon />}
            color="#3b82f6"
            bg="#eff6ff"
            border="#bfdbfe"
            loading={loading}
          />
          <OverviewCard
            label="Total Vendors"
            value={ov.total_vendors}
            icon={<StoreIcon />}
            color="#7c3aed"
            bg="#f5f3ff"
            border="#ddd6fe"
            loading={loading}
            onClick={() => navigate("/vendors")}
          />
          <OverviewCard
            label="Total RFQs"
            value={ov.total_rfqs}
            icon={<FileTextIcon />}
            color="#0891b2"
            bg="#ecfeff"
            border="#a5f3fc"
            loading={loading}
          />
          <OverviewCard
            label="Shortlisted RFQs"
            value={extraStats.shortlisted}
            icon={<FileTextIcon />}
            color="#2563eb"
            bg="#eff6ff"
            border="#bfdbfe"
            loading={loading}
          />
          <OverviewCard
            label="Total Bids"
            value={extraStats.bids}
            icon={<PackageIcon />}
            color="#16a34a"
            bg="#f0fdf4"
            border="#bbf7d0"
            loading={loading}
          />
          <OverviewCard
            label="Approvals Created"
            value={extraStats.approvalsCreated}
            icon={<UsersIcon />}
            color="#7c3aed"
            bg="#f5f3ff"
            border="#ddd6fe"
            loading={loading}
          />
          <OverviewCard
            label="Approvals Not Approved"
            value={extraStats.approvalsNotApproved}
            icon={<MessageIcon />}
            color="#dc2626"
            bg="#fff5f5"
            border="#fecaca"
            loading={loading}
          />
          <OverviewCard
            label="Contact Reqs"
            value={ov.total_contact_requests}
            icon={<MessageIcon />}
            color="#16a34a"
            bg="#f0fdf4"
            border="#bbf7d0"
            loading={loading}
          />
          <OverviewCard
            label="Item Masters"
            value={ov.buyer_item_masters}
            icon={<PackageIcon />}
            color="#d97706"
            bg="#fffbeb"
            border="#fde68a"
            loading={loading}
          />
          <OverviewCard
            label="Admin Users"
            value={ov.admin_users}
            icon={<UsersIcon />}
            color="#dc2626"
            bg="#fff5f5"
            border="#fecaca"
            loading={loading}
          />
        </div>

        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}
        >
          <SectionCard
            title="Vendor Onboarding"
            subtitle={`${vo.total || 0} total vendors`}
            icon={<StoreIcon />}
            accentColor="#7c3aed"
          >
            {loading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} style={{ marginBottom: 14 }}>
                    <Skel w="70%" h={11} />
                    <div style={{ marginTop: 5 }}>
                      <Skel w="100%" h={7} r={6} />
                    </div>
                  </div>
                ))
              : (vo.items || []).map((item) => (
                  <FunnelItem
                    key={item.key}
                    label={item.label}
                    count={item.count}
                    total={vo.total}
                    color={
                      item.key === "submitted"
                        ? "#16a34a"
                        : item.key.startsWith("step4")
                          ? "#7c3aed"
                          : item.key.startsWith("step3")
                            ? "#f59e0b"
                            : item.key.startsWith("step2")
                              ? "#0891b2"
                              : "#94a3b8"
                    }
                  />
                ))}
          </SectionCard>

          <SectionCard
            title="RFQ Status Breakdown"
            subtitle={`${rs.total || 0} total RFQs`}
            icon={<FileTextIcon />}
            accentColor="#0891b2"
          >
            {loading
              ? Array.from({ length: 5 }).map((_, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "10px 0",
                      borderBottom: "1px solid #f8fafc",
                    }}
                  >
                    <Skel w={10} h={10} r={5} />
                    <Skel w="50%" h={12} />
                    <div style={{ marginLeft: "auto" }}>
                      <Skel w={80} h={24} r={8} />
                    </div>
                  </div>
                ))
              : (rs.items || []).map((item) => (
                  <RFQStatusItem key={item.key} item={item} total={rs.total} />
                ))}
          </SectionCard>
        </div>

        <SectionCard
          title="Growth Metrics"
          subtitle="7-day and 30-day growth across all modules"
          icon={<TrendUpIcon />}
          accentColor="#16a34a"
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
              gap: 12,
            }}
          >
            {loading
              ? Array.from({ length: 9 }).map((_, i) => (
                  <div
                    key={i}
                    style={{
                      background: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      borderRadius: 12,
                      padding: "14px 16px",
                    }}
                  >
                    <Skel w="60%" h={18} />
                    <div style={{ marginTop: 8 }}>
                      <Skel w="40%" h={11} />
                    </div>
                    <div style={{ marginTop: 10, display: "flex", gap: 6 }}>
                      <Skel w={58} h={22} r={7} />
                      <Skel w={58} h={22} r={7} />
                    </div>
                  </div>
                ))
              : (ss.quick_stats || []).map((stat) => (
                  <QuickStatCard key={stat.key} stat={stat} loading={false} />
                ))}
          </div>
        </SectionCard>

        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}
        >
          <SectionCard
            title="Master Data"
            subtitle="Reference data across the platform"
            icon={<DatabaseIcon />}
            accentColor="#0891b2"
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 10,
              }}
            >
              {loading
                ? Array.from({ length: 10 }).map((_, i) => (
                    <div
                      key={i}
                      style={{
                        background: "#f8fafc",
                        border: "1px solid #e2e8f0",
                        borderRadius: 9,
                        padding: "11px 14px",
                      }}
                    >
                      <Skel w={40} h={18} />
                      <div style={{ marginTop: 5 }}>
                        <Skel w={80} h={11} />
                      </div>
                    </div>
                  ))
                : MASTER_ITEMS.map((item) => (
                    <div
                      key={item.key}
                      style={{
                        background: `${item.color}0a`,
                        border: `1px solid ${item.color}20`,
                        borderRadius: 9,
                        padding: "11px 14px",
                      }}
                    >
                      <p
                        style={{
                          margin: 0,
                          fontSize: 18,
                          fontWeight: 800,
                          color: item.color,
                          lineHeight: 1,
                        }}
                      >
                        {(md[item.key] ?? 0).toLocaleString()}
                      </p>
                      <p
                        style={{
                          margin: "4px 0 0",
                          fontSize: 11.5,
                          color: "#64748b",
                          fontWeight: 550,
                        }}
                      >
                        {item.label}
                      </p>
                    </div>
                  ))}
            </div>
          </SectionCard>

          <SectionCard
            title="System Health"
            subtitle={
              ss.message ||
              (ss.status === "ok" ? "All systems operational" : "")
            }
            icon={<CloudIcon />}
            accentColor="#16a34a"
          >
            {loading ? (
              <>
                <div style={{ marginBottom: 12 }}>
                  <Skel w="100%" h={80} r={10} />
                </div>
                <Skel w="100%" h={80} r={10} />
              </>
            ) : (
              <>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    marginBottom: 16,
                  }}
                >
                  <StorageBar
                    label="Database (PostgreSQL)"
                    icon={<DatabaseIcon />}
                    enabled={true}
                    sizePretty={ss.summary?.total_db?.pretty}
                    sizeBytes={ss.summary?.total_db?.bytes}
                    color="#3b82f6"
                    extraInfo={ss.summary?.total_db?.engine}
                  />
                  {ss.summary?.s3_storage && (
                    <StorageBar
                      label={ss.summary.s3_storage.label}
                      icon={<CloudIcon />}
                      enabled={ss.summary.s3_storage.enabled}
                      sizePretty={ss.summary.s3_storage.size_pretty}
                      sizeBytes={ss.summary.s3_storage.size_bytes}
                      color="#7c3aed"
                      error={ss.summary.s3_storage.error}
                      extraInfo={`Bucket: ${ss.summary.s3_storage.bucket} · ${ss.summary.s3_storage.object_count?.toLocaleString()} objects`}
                    />
                  )}
                  {ss.summary?.local_media && (
                    <StorageBar
                      label={ss.summary.local_media.label}
                      icon={<DatabaseIcon />}
                      enabled={ss.summary.local_media.enabled}
                      sizePretty={ss.summary.local_media.size_pretty}
                      sizeBytes={ss.summary.local_media.size_bytes}
                      color="#0891b2"
                      error={ss.summary.local_media.error}
                      extraInfo={ss.summary.local_media.path}
                    />
                  )}
                </div>
                {ss.computed_at && (
                  <p
                    style={{
                      margin: 0,
                      fontSize: 11.5,
                      color: "#94a3b8",
                      textAlign: "right",
                    }}
                  >
                    Stats computed: {fmtDate(ss.computed_at)}
                  </p>
                )}
              </>
            )}
          </SectionCard>
        </div>

        <div
          style={{
            background: "#fff",
            borderRadius: 14,
            border: "1px solid #e2e8f0",
            boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "16px 20px 0",
              borderBottom: "1px solid #f1f5f9",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              <div>
                <p
                  style={{
                    margin: 0,
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#0f172a",
                  }}
                >
                  Recent Activity
                </p>
                <p
                  style={{
                    margin: "2px 0 8px",
                    fontSize: 12,
                    color: "#94a3b8",
                  }}
                >
                  Latest records across vendors, RFQs and contact requests
                </p>
              </div>
            </div>
            <div style={{ display: "flex", gap: 0 }}>
              {TAB_LIST.map((tab, i) => (
                <button
                  key={tab}
                  onClick={() => setActTab(i)}
                  style={{
                    padding: "8px 18px",
                    background: "none",
                    border: "none",
                    borderBottom: `2px solid ${actTab === i ? "#3b82f6" : "transparent"}`,
                    color: actTab === i ? "#3b82f6" : "#64748b",
                    fontSize: 13.5,
                    fontWeight: actTab === i ? 700 : 550,
                    cursor: "pointer",
                    transition: "color 0.15s",
                    marginBottom: -1,
                  }}
                >
                  {tab}
                  {i === 0 && ra.vendors?.length ? (
                    <span
                      style={{
                        marginLeft: 6,
                        background: "#eff6ff",
                        color: "#3b82f6",
                        borderRadius: 20,
                        fontSize: 11,
                        fontWeight: 700,
                        padding: "1px 7px",
                      }}
                    >
                      {ra.vendors.length}
                    </span>
                  ) : null}
                  {i === 1 && ra.rfqs?.length ? (
                    <span
                      style={{
                        marginLeft: 6,
                        background: "#ecfeff",
                        color: "#0891b2",
                        borderRadius: 20,
                        fontSize: 11,
                        fontWeight: 700,
                        padding: "1px 7px",
                      }}
                    >
                      {ra.rfqs.length}
                    </span>
                  ) : null}
                  {i === 2 && ra.contact_requests?.length ? (
                    <span
                      style={{
                        marginLeft: 6,
                        background: "#f0fdf4",
                        color: "#16a34a",
                        borderRadius: 20,
                        fontSize: 11,
                        fontWeight: 700,
                        padding: "1px 7px",
                      }}
                    >
                      {ra.contact_requests.length}
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            {actTab === 0 && (
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 13.5,
                }}
              >
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    {["#", "Business Name", "Email", "Stage", "Registered"].map(
                      (h) => (
                        <th
                          key={h}
                          style={{
                            padding: "10px 16px",
                            textAlign: "left",
                            fontSize: 11.5,
                            fontWeight: 700,
                            color: "#64748b",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            borderBottom: "1px solid #e2e8f0",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {h}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {loading
                    ? Array.from({ length: 4 }).map((_, i) => (
                        <tr
                          key={i}
                          style={{ borderBottom: "1px solid #f1f5f9" }}
                        >
                          {[30, 200, 160, 120, 100].map((w, j) => (
                            <td key={j} style={{ padding: "14px 16px" }}>
                              <Skel w={w} h={13} />
                            </td>
                          ))}
                        </tr>
                      ))
                    : (ra.vendors || []).map((v, i) => (
                        <tr
                          key={v.id}
                          className="dash-row"
                          style={{
                            borderBottom: "1px solid #f1f5f9",
                            transition: "background 0.12s",
                          }}
                        >
                          <td
                            style={{
                              padding: "12px 16px",
                              color: "#94a3b8",
                              fontSize: 12,
                            }}
                          >
                            {i + 1}
                          </td>
                          <td style={{ padding: "12px 16px" }}>
                            <div
                              style={{
                                fontWeight: 700,
                                color: "#0f172a",
                                maxWidth: 240,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {v.business_name}
                            </div>
                          </td>
                          <td
                            style={{
                              padding: "12px 16px",
                              color: "#475569",
                              fontSize: 13,
                            }}
                          >
                            {v.email}
                          </td>
                          <td style={{ padding: "12px 16px" }}>
                            <StageBadge
                              stage={v.onboarding_stage}
                              label={v.onboarding_stage_label}
                            />
                          </td>
                          <td
                            style={{
                              padding: "12px 16px",
                              color: "#64748b",
                              fontSize: 12.5,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {fmtShortDate(v.created_at)}
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            )}

            {actTab === 1 && (
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 13.5,
                }}
              >
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    {[
                      "#",
                      "RFQ Number",
                      "Project Name",
                      "Status",
                      "Created By",
                      "Date",
                    ].map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: "10px 16px",
                          textAlign: "left",
                          fontSize: 11.5,
                          fontWeight: 700,
                          color: "#64748b",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          borderBottom: "1px solid #e2e8f0",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading
                    ? Array.from({ length: 4 }).map((_, i) => (
                        <tr
                          key={i}
                          style={{ borderBottom: "1px solid #f1f5f9" }}
                        >
                          {[30, 180, 200, 100, 150, 100].map((w, j) => (
                            <td key={j} style={{ padding: "14px 16px" }}>
                              <Skel w={w} h={13} />
                            </td>
                          ))}
                        </tr>
                      ))
                    : (ra.rfqs || []).map((r, i) => (
                        <tr
                          key={r.id}
                          className="dash-row"
                          style={{
                            borderBottom: "1px solid #f1f5f9",
                            transition: "background 0.12s",
                          }}
                        >
                          <td
                            style={{
                              padding: "12px 16px",
                              color: "#94a3b8",
                              fontSize: 12,
                            }}
                          >
                            {i + 1}
                          </td>
                          <td style={{ padding: "12px 16px" }}>
                            <span
                              style={{
                                fontFamily: "monospace",
                                fontSize: 12.5,
                                fontWeight: 700,
                                color: "#1e40af",
                                background: "#eff6ff",
                                padding: "3px 8px",
                                borderRadius: 6,
                              }}
                            >
                              {r.rfq_number}
                            </span>
                          </td>
                          <td
                            style={{
                              padding: "12px 16px",
                              fontWeight: 600,
                              color: "#0f172a",
                              maxWidth: 220,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {r.project_name}
                          </td>
                          <td style={{ padding: "12px 16px" }}>
                            <RFQBadge
                              status={r.status}
                              label={r.status_label}
                            />
                          </td>
                          <td
                            style={{
                              padding: "12px 16px",
                              color: "#475569",
                              fontSize: 13,
                            }}
                          >
                            {r.created_by}
                          </td>
                          <td
                            style={{
                              padding: "12px 16px",
                              color: "#64748b",
                              fontSize: 12.5,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {fmtShortDate(r.created_at)}
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            )}

            {actTab === 2 && (
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 13.5,
                }}
              >
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    {["#", "Name", "Email", "Company", "Notified", "Date"].map(
                      (h) => (
                        <th
                          key={h}
                          style={{
                            padding: "10px 16px",
                            textAlign: "left",
                            fontSize: 11.5,
                            fontWeight: 700,
                            color: "#64748b",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            borderBottom: "1px solid #e2e8f0",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {h}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {loading
                    ? Array.from({ length: 3 }).map((_, i) => (
                        <tr
                          key={i}
                          style={{ borderBottom: "1px solid #f1f5f9" }}
                        >
                          {[30, 130, 160, 120, 80, 100].map((w, j) => (
                            <td key={j} style={{ padding: "14px 16px" }}>
                              <Skel w={w} h={13} />
                            </td>
                          ))}
                        </tr>
                      ))
                    : (ra.contact_requests || []).map((c, i) => (
                        <tr
                          key={c.id}
                          className="dash-row"
                          style={{
                            borderBottom: "1px solid #f1f5f9",
                            transition: "background 0.12s",
                          }}
                        >
                          <td
                            style={{
                              padding: "12px 16px",
                              color: "#94a3b8",
                              fontSize: 12,
                            }}
                          >
                            {i + 1}
                          </td>
                          <td
                            style={{
                              padding: "12px 16px",
                              fontWeight: 700,
                              color: "#0f172a",
                            }}
                          >
                            {c.name}
                          </td>
                          <td
                            style={{
                              padding: "12px 16px",
                              color: "#475569",
                              fontSize: 13,
                            }}
                          >
                            {c.email}
                          </td>
                          <td
                            style={{ padding: "12px 16px", color: "#64748b" }}
                          >
                            {c.company_name || (
                              <span style={{ color: "#cbd5e1" }}>—</span>
                            )}
                          </td>
                          <td style={{ padding: "12px 16px" }}>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                                padding: "2px 9px",
                                borderRadius: 20,
                                fontSize: 11.5,
                                fontWeight: 600,
                                background: c.is_notified
                                  ? "#f0fdf4"
                                  : "#f8fafc",
                                color: c.is_notified ? "#16a34a" : "#94a3b8",
                                border: `1px solid ${c.is_notified ? "#bbf7d0" : "#e2e8f0"}`,
                              }}
                            >
                              {c.is_notified ? <CheckCircleIcon /> : null}
                              {c.is_notified ? "Sent" : "Pending"}
                            </span>
                          </td>
                          <td
                            style={{
                              padding: "12px 16px",
                              color: "#64748b",
                              fontSize: 12.5,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {fmtShortDate(c.created_at)}
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
